import React, { ReactNode } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface MobileCardProps {
  children: ReactNode;
  variant?: 'default' | 'outlined' | 'elevated' | 'filled';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export const MobileCard: React.FC<MobileCardProps> = ({
  children,
  variant = 'default',
  padding = 'md',
  className = '',
  onClick,
  disabled = false,
  loading = false,
}) => {
  const { isDark } = useTheme();

  // Get variant classes
  const getVariantClasses = () => {
    const baseClasses = 'rounded-2xl transition-all duration-300';

    if (disabled) {
      return `${baseClasses} bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed`;
    }

    switch (variant) {
      case 'outlined':
        return `${baseClasses} bg-transparent border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600`;

      case 'elevated':
        return `${baseClasses} bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl ${
          onClick ? 'hover:shadow-2xl active:scale-[0.98]' : ''
        }`;

      case 'filled':
        return `${baseClasses} bg-gray-50 dark:bg-gray-900 ${
          onClick
            ? 'hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-[0.98]'
            : ''
        }`;

      default:
        return `${baseClasses} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 ${
          onClick ? 'hover:shadow-md active:scale-[0.98]' : ''
        }`;
    }
  };

  // Get padding classes
  const getPaddingClasses = () => {
    switch (padding) {
      case 'none':
        return '';
      case 'sm':
        return 'p-3';
      case 'lg':
        return 'p-6';
      default:
        return 'p-4';
    }
  };

  const Element = onClick ? 'button' : 'div';

  return (
    <Element
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled}
      className={`
        ${getVariantClasses()}
        ${getPaddingClasses()}
        ${
          onClick
            ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20'
            : ''
        }
        ${className}
      `}
    >
      {loading ? (
        <div className='flex items-center justify-center py-8'>
          <div className='w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin'></div>
        </div>
      ) : (
        children
      )}
    </Element>
  );
};

// Content Card Component
interface MobileContentCardProps {
  title?: string;
  subtitle?: string;
  description?: string;
  image?: string;
  badge?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  variant?: 'default' | 'outlined' | 'elevated' | 'filled';
  onClick?: () => void;
  className?: string;
}

export const MobileContentCard: React.FC<MobileContentCardProps> = ({
  title,
  subtitle,
  description,
  image,
  badge,
  actions,
  footer,
  children,
  variant = 'default',
  onClick,
  className = '',
}) => {
  return (
    <MobileCard
      variant={variant}
      padding='none'
      onClick={onClick}
      className={className}
    >
      {/* Image Section */}
      {image && (
        <div className='relative'>
          <img
            src={image}
            alt={title || ''}
            className='w-full h-48 object-cover rounded-t-2xl'
          />

          {/* Badge Overlay */}
          {badge && <div className='absolute top-3 right-3'>{badge}</div>}

          {/* Actions Overlay */}
          {actions && <div className='absolute top-3 left-3'>{actions}</div>}
        </div>
      )}

      {/* Content Section */}
      <div className='p-4'>
        {/* Header */}
        {(title || subtitle || (!image && (badge || actions))) && (
          <div className='flex items-start justify-between mb-3'>
            <div className='flex-1 min-w-0'>
              {title && (
                <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 truncate'>
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className='text-sm text-gray-500 dark:text-gray-400 truncate'>
                  {subtitle}
                </p>
              )}
            </div>

            {!image && (badge || actions) && (
              <div className='flex items-center gap-2 mr-3'>
                {badge}
                {actions}
              </div>
            )}
          </div>
        )}

        {/* Description */}
        {description && (
          <p className='text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-3'>
            {description}
          </p>
        )}

        {/* Custom Content */}
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div className='px-4 pb-4 pt-0 border-t border-gray-200 dark:border-gray-700 mt-3'>
          <div className='pt-3'>{footer}</div>
        </div>
      )}
    </MobileCard>
  );
};

// Grid Layout Component
interface MobileGridProps {
  children: ReactNode;
  columns?: 1 | 2 | 3;
  gap?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const MobileGrid: React.FC<MobileGridProps> = ({
  children,
  columns = 1,
  gap = 'md',
  className = '',
}) => {
  // Get grid classes
  const getGridClasses = () => {
    const columnClasses = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 sm:grid-cols-2',
      3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    };

    const gapClasses = {
      sm: 'gap-3',
      md: 'gap-4',
      lg: 'gap-6',
    };

    return `grid ${columnClasses[columns]} ${gapClasses[gap]}`;
  };

  return <div className={`${getGridClasses()} ${className}`}>{children}</div>;
};

// List Container Component
interface MobileListProps {
  children: ReactNode;
  divided?: boolean;
  padding?: boolean;
  className?: string;
}

export const MobileList: React.FC<MobileListProps> = ({
  children,
  divided = false,
  padding = true,
  className = '',
}) => {
  return (
    <div
      className={`
      ${padding ? 'p-1' : ''}
      ${divided ? 'divide-y divide-gray-200 dark:divide-gray-700' : ''}
      ${className}
    `}
    >
      {children}
    </div>
  );
};

// List Item Component
interface MobileListItemProps {
  children: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  title?: string;
  subtitle?: string;
  description?: string;
  badge?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export const MobileListItem: React.FC<MobileListItemProps> = ({
  children,
  leftIcon,
  rightIcon,
  title,
  subtitle,
  description,
  badge,
  onClick,
  disabled = false,
  className = '',
}) => {
  const Element = onClick ? 'button' : 'div';

  return (
    <Element
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`
        w-full p-4 flex items-center gap-4 text-right transition-all duration-300
        ${
          onClick && !disabled
            ? 'hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 rounded-xl'
            : ''
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
    >
      {/* Right Icon/Avatar */}
      {leftIcon && <div className='flex-shrink-0'>{leftIcon}</div>}

      {/* Content */}
      <div className='flex-1 min-w-0'>
        {title && (
          <div className='flex items-center justify-between mb-1'>
            <h4 className='text-base font-medium text-gray-900 dark:text-gray-100 truncate'>
              {title}
            </h4>
            {badge && <div className='mr-2'>{badge}</div>}
          </div>
        )}

        {subtitle && (
          <p className='text-sm text-gray-500 dark:text-gray-400 truncate mb-1'>
            {subtitle}
          </p>
        )}

        {description && (
          <p className='text-sm text-gray-600 dark:text-gray-300 line-clamp-2'>
            {description}
          </p>
        )}

        {children}
      </div>

      {/* Left Icon/Action */}
      {rightIcon && <div className='flex-shrink-0'>{rightIcon}</div>}
    </Element>
  );
};

// Container Component for consistent spacing
interface MobileContainerProps {
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  padding?: boolean;
  className?: string;
}

export const MobileContainer: React.FC<MobileContainerProps> = ({
  children,
  maxWidth = 'lg',
  padding = true,
  className = '',
}) => {
  const getMaxWidthClasses = () => {
    switch (maxWidth) {
      case 'sm':
        return 'max-w-sm';
      case 'md':
        return 'max-w-md';
      case 'lg':
        return 'max-w-4xl';
      case 'xl':
        return 'max-w-6xl';
      case 'full':
        return 'w-full';
      default:
        return 'max-w-4xl';
    }
  };

  return (
    <div
      className={`
      ${getMaxWidthClasses()}
      mx-auto
      ${padding ? 'px-4 py-6' : ''}
      ${className}
    `}
    >
      {children}
    </div>
  );
};

export default {
  MobileCard,
  MobileContentCard,
  MobileGrid,
  MobileList,
  MobileListItem,
  MobileContainer,
};
